import { supabase } from '@/lib/supabase';
import { executeSecureQuery } from '@/lib/supabaseAuth';
import { NetworkErrorHandler, safeEntityCall } from '@/components/utils/errorHandler';
import { categoryCache } from './CacheManager';
import { categoryLogger } from './ErrorLogger';
import type {
  CategoryFilters,
  CategoryWithAggregatedData,
  CategoryProgressData,
  PieChartCategoryData,
  CategoryStatistics,
  DashboardCategoryData,
  CategoryServiceResponse,
  CategoryServiceError,
  FinancialTransaction,
  SubcategoryWithTotals
} from '@/types/CategoryTypes';
import { CategoryServiceErrorType } from '@/types/CategoryTypes';

/**
 * Serviço robusto para gerenciamento de dados de categorias e subcategorias
 * 
 * Este serviço fornece uma API unificada para:
 * - Buscar categorias e subcategorias com dados agregados
 * - Calcular totais planejados e executados
 * - Preparar dados para componentes específicos (ExpensePieChart, CategoryProgress)
 * - Gerenciar cache inteligente para otimização de performance
 * - Tratar erros de forma robusta
 */
export class CategoryDataService {
  private static instance: CategoryDataService;

  /**
   * Singleton pattern para garantir uma única instância
   */
  public static getInstance(): CategoryDataService {
    if (!CategoryDataService.instance) {
      CategoryDataService.instance = new CategoryDataService();
    }
    return CategoryDataService.instance;
  }

  /**
   * Busca dados completos de categorias para o dashboard
   * 
   * @param filters - Filtros para busca de categorias
   * @returns Dados completos formatados para o dashboard
   */
  public async getDashboardData(filters: CategoryFilters): Promise<CategoryServiceResponse<DashboardCategoryData>> {
    const startTime = Date.now();
    categoryLogger.info('Iniciando busca de dados do dashboard', { filters }, 'getDashboardData');

    try {
      const cacheKey = this.generateCacheKey('dashboard', filters);
      const cachedData = categoryCache.get<DashboardCategoryData>(cacheKey);
      
      if (cachedData) {
        categoryLogger.debug('Dados encontrados no cache', { cacheKey }, 'getDashboardData');
        return {
          data: cachedData,
          success: true,
          cached: true,
          timestamp: Date.now()
        };
      }

      categoryLogger.debug('Cache miss, buscando dados do Supabase', { cacheKey }, 'getDashboardData');

      // Buscar categorias principais com dados planejados
      const categories = await this.fetchCategoriesWithPlannedData(filters);
      categoryLogger.debug('Categorias carregadas', { count: categories.length }, 'getDashboardData');
      
      // Buscar transações para calcular valores executados
      const transactions = await this.fetchTransactions(filters);
      categoryLogger.debug('Transações carregadas', { count: transactions.length }, 'getDashboardData');
      
      // Processar dados agregados
      const aggregatedCategories = await this.processAggregatedData(categories, transactions, filters);
      categoryLogger.debug('Dados agregados processados', { count: aggregatedCategories.length }, 'getDashboardData');
      
      // Calcular estatísticas
      const statistics = this.calculateStatistics(aggregatedCategories);
      
      // Preparar dados para componentes específicos
      const pieChartData = this.preparePieChartData(aggregatedCategories);
      const progressData = this.prepareProgressData(aggregatedCategories);

      const dashboardData: DashboardCategoryData = {
        categories: aggregatedCategories,
        statistics,
        pie_chart_data: pieChartData,
        progress_data: progressData,
        last_updated: new Date().toISOString()
      };

      // Armazenar no cache
      categoryCache.set(cacheKey, dashboardData, undefined, [
        `account_${filters.account_id}`,
        filters.monthly_plan_id ? `plan_${filters.monthly_plan_id}` : 'no_plan'
      ]);

      const duration = Date.now() - startTime;
      categoryLogger.info('Dados do dashboard carregados com sucesso', { 
        duration, 
        categoriesCount: aggregatedCategories.length,
        transactionsCount: transactions.length 
      }, 'getDashboardData');

      return {
        data: dashboardData,
        success: true,
        cached: false,
        timestamp: Date.now()
      };

    } catch (error) {
      const duration = Date.now() - startTime;
      categoryLogger.error('Erro ao buscar dados do dashboard', error as Error, { 
        filters, 
        duration 
      }, 'getDashboardData');
      
      return this.handleError(error);
    }
  }

  /**
   * Busca dados formatados para gráfico de pizza
   * 
   * @param filters - Filtros para busca de categorias
   * @returns Dados formatados para PieChart
   */
  public async getPieChartData(filters: CategoryFilters): Promise<CategoryServiceResponse<PieChartCategoryData[]>> {
    const startTime = Date.now();
    categoryLogger.info('Iniciando busca de dados para gráfico de pizza', { filters }, 'getPieChartData');

    try {
      const cacheKey = this.generateCacheKey('pie-chart', filters);
      const cachedData = categoryCache.get<PieChartCategoryData[]>(cacheKey);
      
      if (cachedData) {
        categoryLogger.debug('Dados do gráfico encontrados no cache', { cacheKey }, 'getPieChartData');
        return {
          data: cachedData,
          success: true,
          cached: true,
          timestamp: Date.now()
        };
      }

      categoryLogger.debug('Cache miss, processando dados para gráfico', { cacheKey }, 'getPieChartData');

      // Buscar dados do dashboard (que já tem cache próprio)
      const dashboardResponse = await this.getDashboardData(filters);
      
      if (!dashboardResponse.success || !dashboardResponse.data) {
        categoryLogger.warn('Falha ao obter dados do dashboard para gráfico', { filters }, 'getPieChartData');
        throw new Error('Falha ao obter dados do dashboard');
      }

      const pieChartData = dashboardResponse.data.pie_chart_data;
      
      // Armazenar no cache
      categoryCache.set(cacheKey, pieChartData, undefined, [
        `account_${filters.account_id}`,
        filters.monthly_plan_id ? `plan_${filters.monthly_plan_id}` : 'no_plan'
      ]);

      const duration = Date.now() - startTime;
      categoryLogger.info('Dados do gráfico de pizza processados com sucesso', { 
        duration, 
        dataCount: pieChartData.length 
      }, 'getPieChartData');

      return {
        data: pieChartData,
        success: true,
        cached: false,
        timestamp: Date.now()
      };

    } catch (error) {
      const duration = Date.now() - startTime;
      categoryLogger.error('Erro ao buscar dados do gráfico de pizza', error as Error, { 
        filters, 
        duration 
      }, 'getPieChartData');
      
      return this.handleError(error);
    }
  }

  /**
   * Busca dados formatados para componente de progresso
   * 
   * @param filters - Filtros para busca de categorias
   * @returns Dados formatados para CategoryProgress
   */
  public async getProgressData(filters: CategoryFilters): Promise<CategoryServiceResponse<CategoryProgressData[]>> {
    const startTime = Date.now();
    categoryLogger.info('Iniciando busca de dados de progresso', { filters }, 'getProgressData');

    try {
      const cacheKey = this.generateCacheKey('progress', filters);
      const cachedData = categoryCache.get<CategoryProgressData[]>(cacheKey);
      
      if (cachedData) {
        categoryLogger.debug('Dados de progresso encontrados no cache', { cacheKey }, 'getProgressData');
        return {
          data: cachedData,
          success: true,
          cached: true,
          timestamp: Date.now()
        };
      }

      categoryLogger.debug('Cache miss, processando dados de progresso', { cacheKey }, 'getProgressData');

      // Buscar dados do dashboard (que já tem cache próprio)
      const dashboardResponse = await this.getDashboardData(filters);
      
      if (!dashboardResponse.success || !dashboardResponse.data) {
        categoryLogger.warn('Falha ao obter dados do dashboard para progresso', { filters }, 'getProgressData');
        throw new Error('Falha ao obter dados do dashboard');
      }

      const progressData = dashboardResponse.data.progress_data;
      
      // Armazenar no cache
      categoryCache.set(cacheKey, progressData, undefined, [
        `account_${filters.account_id}`,
        filters.monthly_plan_id ? `plan_${filters.monthly_plan_id}` : 'no_plan'
      ]);

      const duration = Date.now() - startTime;
      categoryLogger.info('Dados de progresso processados com sucesso', { 
        duration, 
        dataCount: progressData.length 
      }, 'getProgressData');

      return {
        data: progressData,
        success: true,
        cached: false,
        timestamp: Date.now()
      };

    } catch (error) {
      const duration = Date.now() - startTime;
      categoryLogger.error('Erro ao buscar dados de progresso', error as Error, { 
        filters, 
        duration 
      }, 'getProgressData');
      
      return this.handleError(error);
    }
  }

  /**
   * Limpa cache específico por padrão de chave
   */
  public clearCacheByPattern(pattern: string): void {
    categoryCache.deleteByPattern(pattern);
  }

  /**
   * Limpa cache por tags
   */
  public clearCacheByTag(tag: string): void {
    categoryCache.deleteByTag(tag);
  }

  /**
   * Limpa todo o cache
   */
  public clearCache(): void {
    categoryCache.clear();
  }

  /**
   * Força o recarregamento de dados de categorias para uma conta específica
   * Limpa o cache relacionado e recarrega os dados do banco
   */
  public async forceReloadCategoryData(accountId: string, monthlyPlanId?: string): Promise<void> {
    categoryLogger.info('Forçando recarregamento de dados de categorias', { accountId, monthlyPlanId }, 'forceReloadCategoryData');
    
    // Limpar cache específico da conta
    this.clearCacheByTag(`account_${accountId}`);
    
    if (monthlyPlanId) {
      this.clearCacheByTag(`plan_${monthlyPlanId}`);
    }
    
    // Limpar também por padrões específicos
    this.clearCacheByPattern(`dashboard_${accountId}`);
    this.clearCacheByPattern(`progress_${accountId}`);
    this.clearCacheByPattern(`piechart_${accountId}`);
    
    categoryLogger.info('Cache limpo com sucesso', { accountId, monthlyPlanId }, 'forceReloadCategoryData');
  }

  /**
   * Invalida cache de subcategorias específicas por nome
   * Útil quando uma subcategoria é renomeada (ex: Transporte -> Carro)
   */
  public invalidateSubcategoryCache(accountId: string, subcategoryName: string): void {
    categoryLogger.info('Invalidando cache de subcategoria', { accountId, subcategoryName }, 'invalidateSubcategoryCache');
    
    // Limpar cache por tags relacionadas à conta
    this.clearCacheByTag(`account_${accountId}`);
    
    // Limpar cache por padrões que podem conter a subcategoria
    this.clearCacheByPattern(`.*${accountId}.*`);
    
    categoryLogger.info('Cache de subcategoria invalidado', { accountId, subcategoryName }, 'invalidateSubcategoryCache');
  }

  /**
   * Busca categorias principais com dados do plano mensal
   */
  private async fetchCategoriesWithPlannedData(filters: CategoryFilters): Promise<any[]> {
    return executeSecureQuery(async () => {
      // Primeiro, buscar todas as categorias
      let categoriesQuery = supabase
        .from('financial_categories')
        .select('*')
        .eq('account_id', filters.account_id);

      if (filters.type) {
        categoriesQuery = categoriesQuery.eq('type', filters.type);
      }

      const { data: categories, error: categoriesError } = await categoriesQuery;
      if (categoriesError) throw categoriesError;

      if (!categories || categories.length === 0) {
        return [];
      }

      // Buscar dados planejados para essas categorias
      let plannedQuery = supabase
        .from('planned_categories')
        .select('*')
        .in('category_id', categories.map((c: any) => c.id));

      if (filters.monthly_plan_id) {
        plannedQuery = plannedQuery.eq('monthly_plan_id', filters.monthly_plan_id);
      }

      const { data: plannedData, error: plannedError } = await plannedQuery;
      if (plannedError) throw plannedError;

      // Combinar dados de categorias com dados planejados
      return categories.map((category: any) => {
        const plannedCategory = plannedData?.find((pc: any) => pc.category_id === category.id);
        
        return {
          ...category,
          planned_amount: parseFloat(plannedCategory?.planned_amount || '0'),
          spent_amount: parseFloat(plannedCategory?.spent_amount || '0'),
          received_amount: parseFloat(plannedCategory?.received_amount || '0'),
          planned_category_id: plannedCategory?.id || null,
          monthly_plan_id: plannedCategory?.monthly_plan_id || null
        };
      });
    });
  }

  /**
   * Busca transações financeiras para cálculo de valores executados
   */
  private async fetchTransactions(filters: CategoryFilters): Promise<FinancialTransaction[]> {
    return executeSecureQuery(async () => {
      let query = supabase
        .from('financial_records')
        .select(`
          id,
          amount,
          date,
          category_id,
          account_id,
          monthly_plan_id,
          description,
          is_investment
        `)
        .eq('account_id', filters.account_id)
        .eq('is_investment', false)
        .eq('status', 'affected'); // Apenas transações efetivadas

      if (filters.monthly_plan_id) {
        // Buscar transações que têm o monthly_plan_id correto OU são do período mas não têm monthly_plan_id
        query = query.or(`monthly_plan_id.eq.${filters.monthly_plan_id},monthly_plan_id.is.null`);
      }

      if (filters.date_range) {
        query = query
          .gte('date', filters.date_range.start_date)
          .lte('date', filters.date_range.end_date);
        
        console.log('🔍 CategoryDataService - Filtrando transações por data:', {
          start_date: filters.date_range.start_date,
          end_date: filters.date_range.end_date
        });
      }

      const { data, error } = await query;
      if (error) throw error;
      
      console.log('🔍 CategoryDataService - Transações encontradas:', data?.length || 0);
      if (data && data.length > 0) {
        console.log('🔍 CategoryDataService - Primeiras transações:', data.slice(0, 3).map(t => ({
          id: t.id,
          date: t.date,
          amount: t.amount,
          category_id: t.category_id
        })));
      }

      return data?.map((record: any) => ({
        ...record,
        amount: parseFloat(record.amount || '0')
      })) || [];
    });
  }

  /**
   * Busca subcategorias para categorias específicas
   */
  private async fetchSubcategories(parentIds: string[], accountId: string, monthlyPlanId?: string): Promise<any[]> {
    // Validar se há IDs válidos antes de fazer a consulta
    if (parentIds.length === 0) {
      // Array vazio é normal quando não há categorias principais com subcategorias
      return [];
    }
    
    if (parentIds.some(id => !id || id === 'undefined')) {
      categoryLogger.warn('fetchSubcategories: IDs de categoria inválidos detectados', { parentIds });
      return [];
    }

    return executeSecureQuery(async () => {
      // Buscar subcategorias
      const { data: subcategories, error } = await supabase
        .from('financial_categories')
        .select('*')
        .eq('account_id', accountId)
        .in('parent_id', parentIds);

      if (error) throw error;
      
      if (!subcategories || subcategories.length === 0) {
        return [];
      }

      // Buscar dados planejados para as subcategorias
      let plannedQuery = supabase
        .from('planned_categories')
        .select('*')
        .in('category_id', subcategories.map((c: any) => c.id));

      if (monthlyPlanId) {
        plannedQuery = plannedQuery.eq('monthly_plan_id', monthlyPlanId);
      }

      const { data: plannedData, error: plannedError } = await plannedQuery;
      if (plannedError) throw plannedError;

      // Combinar dados de subcategorias com dados planejados
      return subcategories.map((subcategory: any) => {
        const plannedCategory = plannedData?.find((pc: any) => pc.category_id === subcategory.id);
        
        return {
          ...subcategory,
          planned_amount: plannedCategory?.planned_amount || null,
          spent_amount: parseFloat(plannedCategory?.spent_amount || '0'),
          received_amount: parseFloat(plannedCategory?.received_amount || '0'),
          planned_category_id: plannedCategory?.id || null,
          monthly_plan_id: plannedCategory?.monthly_plan_id || null
        };
      });
    });
  }

  /**
   * Processa dados agregados incluindo subcategorias
   */
  private async processAggregatedData(
    categories: any[],
    transactions: FinancialTransaction[],
    filters: CategoryFilters
  ): Promise<CategoryWithAggregatedData[]> {
    
    // Buscar subcategorias - filtrar IDs válidos para evitar undefined
    const mainCategoryIds = categories
      .filter(cat => !cat.parent_id && cat.id) // Garantir que cat.id existe
      .map(cat => cat.id)
      .filter(id => id !== undefined && id !== null); // Filtrar valores inválidos
    
    const subcategories = await this.fetchSubcategories(mainCategoryIds, filters.account_id, filters.monthly_plan_id);

    // Processar cada categoria principal
    const processedCategories = categories
      .filter(cat => !cat.parent_id)
      .map(mainCategory => {
        // Encontrar subcategorias desta categoria
        const categorySubcategories = subcategories.filter(sub => sub.parent_id === mainCategory.id);

        const hasPlannedThisMonth = (mainCategory.monthly_plan_id && filters.monthly_plan_id && mainCategory.monthly_plan_id === filters.monthly_plan_id)
          || categorySubcategories.some(sub => sub.monthly_plan_id && filters.monthly_plan_id && sub.monthly_plan_id === filters.monthly_plan_id);
        if (!hasPlannedThisMonth) {
          return null as any;
        }

        // DEBUG: Log específico para categoria Lazer
        if (mainCategory.name === 'Lazer') {
          console.log('🔍 [CategoryDataService] Processando categoria Lazer:', {
            mainCategory: {
              id: mainCategory.id,
              name: mainCategory.name,
              planned_amount: mainCategory.planned_amount
            },
            subcategoriesCount: categorySubcategories.length,
            subcategories: categorySubcategories.map(sub => ({
              id: sub.id,
              name: sub.name,
              planned_amount: sub.planned_amount
            }))
          });
        }

        // Calcular totais da categoria principal
        const mainCategoryTransactions = transactions.filter(t => t.category_id === mainCategory.id);
        const mainCategorySpent = mainCategoryTransactions
          .reduce((sum, t) => sum + Math.abs(t.amount), 0);

        // Calcular totais das subcategorias
        const subcategoriesWithTotals: SubcategoryWithTotals[] = categorySubcategories.map(subcat => {
          const subcatTransactions = transactions.filter(t => t.category_id === subcat.id);
          const subcatSpent = subcatTransactions.reduce((sum, t) => sum + Math.abs(t.amount), 0);
          const subcatPlanned = parseFloat(subcat.planned_amount || '0');
          
          // DEBUG: Log detalhado das subcategorias de Lazer
          if (mainCategory.name === 'Lazer') {
            console.log(`🔍 [CategoryDataService] Subcategoria ${subcat.name}:`, {
              id: subcat.id,
              name: subcat.name,
              planned_amount_raw: subcat.planned_amount,
              planned_amount_parsed: subcatPlanned,
              spent: subcatSpent,
              transactions_count: subcatTransactions.length
            });
          }
          
          return {
            ...subcat,
            planned_amount: subcatPlanned,
            spent_amount: 0,
            received_amount: 0,
            total_spent: subcatSpent,
            percentage: subcatPlanned > 0 ? (subcatSpent / subcatPlanned) * 100 : 0,
            transactions: subcatTransactions.map(t => ({
              id: t.id,
              description: t.description,
              date: t.date,
              amount: Math.abs(t.amount),
              category_id: t.category_id,
              account_id: t.account_id,
              monthly_plan_id: t.monthly_plan_id,
              is_investment: t.is_investment
            }))
          };
        });

        const subcategoriesTotal = subcategoriesWithTotals.reduce((sum, sub) => sum + sub.total_spent, 0);
        const subcategoriesPlannedTotal = subcategoriesWithTotals.reduce((sum, sub) => sum + sub.planned_amount, 0);

        // Totais finais - incluir valores planejados das subcategorias
        const totalSpent = mainCategorySpent + subcategoriesTotal;
        const mainCategoryPlanned = parseFloat(mainCategory.planned_amount || '0');
        const totalPlanned = mainCategoryPlanned + subcategoriesPlannedTotal;
        const percentage = totalPlanned > 0 ? (totalSpent / totalPlanned) * 100 : 0;

        // DEBUG: Log específico para categoria Lazer - cálculos finais
        if (mainCategory.name === 'Lazer') {
          console.log('🔍 [CategoryDataService] Cálculos finais para Lazer:', {
            mainCategorySpent,
            subcategoriesTotal,
            totalSpent,
            mainCategoryPlanned,
            subcategoriesPlannedTotal,
            totalPlanned,
            percentage
          });
        }

        return {
          ...mainCategory,
          total_planned: totalPlanned,
          total_spent: totalSpent,
          total_received: mainCategory.type === 'income' ? totalSpent : 0,
          percentage: Math.round(percentage * 100) / 100,
          has_subcategories: subcategoriesWithTotals.length > 0,
          subcategories: subcategoriesWithTotals,
          transactions: mainCategoryTransactions.map(t => ({
            id: t.id,
            description: t.description,
            date: t.date,
            amount: Math.abs(t.amount),
            category_id: t.category_id,
            account_id: t.account_id,
            monthly_plan_id: t.monthly_plan_id,
            is_investment: t.is_investment
          }))
        };
      });

    return processedCategories.filter(Boolean);
  }

  /**
   * Calcula estatísticas gerais das categorias
   */
  private calculateStatistics(categories: CategoryWithAggregatedData[]): CategoryStatistics {
    const totalSubcategories = categories.reduce((sum, cat) => sum + cat.subcategories.length, 0);
    const totalPlanned = categories.reduce((sum, cat) => sum + cat.total_planned, 0);
    const totalSpent = categories.reduce((sum, cat) => sum + cat.total_spent, 0);
    const totalReceived = categories
      .filter(cat => cat.type === 'income')
      .reduce((sum, cat) => sum + cat.total_received, 0);

    const categoriesOverBudget = categories.filter(cat => cat.percentage > 100).length;
    const categoriesUnderBudget = categories.filter(cat => cat.percentage <= 100).length;
    
    const averageCompletionRate = categories.length > 0 
      ? categories.reduce((sum, cat) => sum + cat.percentage, 0) / categories.length 
      : 0;

    return {
      total_categories: categories.length,
      total_subcategories: totalSubcategories,
      total_planned: totalPlanned,
      total_spent: totalSpent,
      total_received: totalReceived,
      categories_over_budget: categoriesOverBudget,
      categories_under_budget: categoriesUnderBudget,
      average_completion_rate: Math.round(averageCompletionRate * 100) / 100
    };
  }

  /**
   * Prepara dados para o componente ExpensePieChart
   */
  private preparePieChartData(categories: CategoryWithAggregatedData[]): PieChartCategoryData[] {
    return categories
      .filter(cat => cat.type === 'expense' && cat.total_spent > 0)
      .map(cat => ({
        id: cat.id,
        name: cat.name,
        value: cat.total_spent,
        planned: cat.total_planned,
        percentage: cat.total_planned > 0 ? (cat.total_spent / cat.total_planned) * 100 : 0,
        records: (cat.transactions || []).map(t => ({
          id: t.id,
          description: t.description,
          date: t.date,
          amount: t.amount
        })),
        subcategories: cat.subcategories.map(sub => ({
          id: sub.id,
          name: sub.name,
          totalSpent: sub.total_spent,
          planned: sub.planned_amount,
          percentage: sub.planned_amount > 0 ? (sub.total_spent / sub.planned_amount) * 100 : 0,
          records: (sub.transactions || []).map(t => ({
            id: t.id,
            description: t.description,
            date: t.date,
            amount: t.amount
          }))
        })),
        hasSubcategories: cat.has_subcategories
      }));
  }

  /**
   * Prepara dados para o componente CategoryProgress
   */
  private prepareProgressData(categories: CategoryWithAggregatedData[]): CategoryProgressData[] {
    return categories.map(cat => ({
      id: cat.id,
      name: cat.name,
      type: cat.type,
      planned: cat.total_planned,
      spent: cat.total_spent,
      received: cat.total_received,
      percentage: cat.percentage,
      parent_id: cat.parent_id,
      children: cat.subcategories.map(sub => ({
        id: sub.id,
        name: sub.name,
        type: sub.type,
        planned: sub.planned_amount,
        spent: sub.total_spent,
        received: sub.received_amount,
        percentage: sub.percentage,
        parent_id: sub.parent_id
      }))
    }));
  }

  /**
   * Gera chave única para cache baseada nos filtros
   */
  private generateCacheKey(prefix: string, filters: CategoryFilters): string {
    const filterString = JSON.stringify(filters);
    return `${prefix}_${btoa(filterString)}`;
  }

  /**
   * Trata erros de forma padronizada
   */
  private handleError(error: any): CategoryServiceResponse<any> {
    categoryLogger.error('Erro no CategoryDataService', error as Error, {}, 'handleError');

    let errorType: CategoryServiceErrorType = CategoryServiceErrorType.UNKNOWN_ERROR;
    let message = 'Erro desconhecido ao buscar dados de categorias';

    if (error.message?.includes('network') || error.message?.includes('fetch')) {
      errorType = CategoryServiceErrorType.NETWORK_ERROR;
      message = 'Erro de conexão. Verifique sua internet e tente novamente.';
    } else if (error.message?.includes('permission') || error.message?.includes('unauthorized')) {
      errorType = CategoryServiceErrorType.PERMISSION_ERROR;
      message = 'Você não tem permissão para acessar estes dados.';
    } else if (error.message?.includes('not found')) {
      errorType = CategoryServiceErrorType.NOT_FOUND;
      message = 'Dados não encontrados.';
    } else if (error.message?.includes('validation')) {
      errorType = CategoryServiceErrorType.VALIDATION_ERROR;
      message = 'Dados inválidos fornecidos.';
    }

    const serviceError: CategoryServiceError = {
      type: errorType,
      message,
      details: error,
      timestamp: Date.now()
    };

    return {
      data: null,
      success: false,
      error: message,
      timestamp: Date.now()
    };
  }
}

// Exportar instância singleton
export const categoryDataService = CategoryDataService.getInstance();
